/* Owns the one WebSocket the tab holds open: handshake auth, subscription set,
   application-level ping/pong, exponential backoff with jitter and close-code
   handling. Transport only — no React and no TanStack Query live here, so the
   cache mapping in `ws/useServerFeed.ts` can be tested without a socket and this
   file can be tested without a renderer. */

import { WS_CLOSE_CODES, WS_LIMITS, WS_SERVER_EVENT_TYPES, type WsClientMessage, type WsServerMessage } from '@sentinelac/shared'

import { API_ROOT, refreshAccessToken } from './api.js'
import { getAccessToken } from './auth-store.js'

export type ConnectionState = 'connecting' | 'open' | 'reconnecting' | 'closed'

export interface WsPublicState {
  connection: ConnectionState
  subscriptions: ReadonlySet<string>
}

/** The slice of `WebSocket` the client actually uses, so a test can supply a fake. */
export interface SocketLike {
  readyState: number
  send(data: string): void
  close(code?: number, reason?: string): void
  onopen: ((event: unknown) => void) | null
  onmessage: ((event: { data: unknown }) => void) | null
  onclose: ((event: { code: number; reason?: string }) => void) | null
  onerror: ((event: unknown) => void) | null
}

export interface WsClientOptions {
  /** Absolute or root-relative `/v1/ws` path; the token is appended as a query parameter. */
  path?: string
  getToken?: () => string | null
  refresh?: () => Promise<string>
  onSessionLost?: () => void
  createSocket?: (url: string) => SocketLike
  setTimer?: (fn: () => void, ms: number) => number
  clearTimer?: (handle: number) => void
  /** Injectable so the jitter is deterministic under test. */
  random?: () => number
}

type MessageHandler = (message: WsServerMessage) => void

const SOCKET_OPEN = 1

const KNOWN_EVENT_TYPES: ReadonlySet<string> = new Set(WS_SERVER_EVENT_TYPES)

function defaultCreateSocket(url: string): SocketLike {
  return new WebSocket(url) as unknown as SocketLike
}

/**
 * Turns a root-relative API path into an absolute ws:// or wss:// URL. The
 * browser `WebSocket` constructor cannot set headers, so the access token rides
 * as `?token=` — the one place it appears in a URL (API.md §21.1; pino redacts
 * `req.query.token` server-side).
 */
function socketUrl(path: string, token: string): string {
  const base = path.startsWith('http') || path.startsWith('ws') ? path : `${location.origin}${path}`
  const url = new URL(base)
  url.protocol = url.protocol === 'https:' || url.protocol === 'wss:' ? 'wss:' : 'ws:'
  url.searchParams.set('token', token)
  return url.toString()
}

export class WsClient {
  private readonly options: Required<Omit<WsClientOptions, 'path'>> & { path: string }

  private socket: SocketLike | null = null
  private connection: ConnectionState = 'closed'
  /** What the app wants to be subscribed to; the server confirms the authorised subset. */
  private desired = new Set<string>()
  private confirmed = new Set<string>()
  private snapshot: WsPublicState = { connection: 'closed', subscriptions: new Set() }

  private backoffIndex = 0
  private reconnectTimer: number | null = null
  private pingTimer: number | null = null
  private pongTimer: number | null = null
  private subscribeTimer: number | null = null
  /** Set by `close()`; suppresses the reconnect the close handler would otherwise schedule. */
  private stopped = true

  private readonly messageHandlers = new Set<MessageHandler>()
  private readonly stateListeners = new Set<() => void>()

  constructor(options: WsClientOptions = {}) {
    this.options = {
      path: options.path ?? `${API_ROOT}/ws`,
      getToken: options.getToken ?? getAccessToken,
      refresh: options.refresh ?? refreshAccessToken,
      onSessionLost: options.onSessionLost ?? (() => undefined),
      createSocket: options.createSocket ?? defaultCreateSocket,
      setTimer: options.setTimer ?? ((fn, ms) => setTimeout(fn, ms) as unknown as number),
      clearTimer: options.clearTimer ?? ((handle) => clearTimeout(handle)),
      random: options.random ?? Math.random,
    }
  }

  // ───────────────────────────── public surface ──────────────────────────────

  getState = (): WsPublicState => this.snapshot

  subscribeState = (listener: () => void): (() => void) => {
    this.stateListeners.add(listener)
    return () => {
      this.stateListeners.delete(listener)
    }
  }

  onMessage(handler: MessageHandler): () => void {
    this.messageHandlers.add(handler)
    return () => {
      this.messageHandlers.delete(handler)
    }
  }

  /** Called by `AppShell` once `GET /v1/auth/me` has produced a session. */
  connect(): void {
    this.stopped = false
    if (this.socket || this.connection === 'connecting') return
    this.open()
  }

  /** Normal closure (code 1000) — no reconnect, used on logout and unmount. */
  close(): void {
    this.stopped = true
    this.clearAllTimers()
    const socket = this.socket
    this.socket = null
    this.confirmed = new Set()
    this.detach(socket, WS_CLOSE_CODES.NORMAL, 'client closed')
    this.setConnection('closed')
  }

  /** The manual `server.reconnect` action in the StatusStrip. */
  reconnectNow(): void {
    this.clearAllTimers()
    this.backoffIndex = 0
    this.stopped = false
    const socket = this.socket
    this.socket = null
    this.detach(socket, WS_CLOSE_CODES.NORMAL, 'manual reconnect')
    this.open()
  }

  /**
   * Replaces the desired subscription set. Debounced 250 ms because navigating
   * between server pages changes it twice in quick succession and the socket is
   * capped at 20 inbound messages per second (SPEC §10.5).
   */
  setSubscriptions(serverIds: readonly string[]): void {
    const next = new Set(serverIds)
    if (next.size === this.desired.size && [...next].every((id) => this.desired.has(id))) return
    this.desired = next
    this.clearTimer('subscribe')
    this.subscribeTimer = this.options.setTimer(() => {
      this.subscribeTimer = null
      this.flushSubscriptions()
    }, 250)
  }

  /** Proactive refresh path: re-auth in place rather than tearing the socket down. */
  sendAuth(token: string): void {
    this.send({ op: 'auth', token })
  }

  // ───────────────────────────── internals ───────────────────────────────────

  /**
   * Detaches a socket we are abandoning. Without nulling the handlers first the
   * browser still fires `onclose` after our own `close()`, which would schedule
   * a second reconnect on top of the one we just started.
   */
  private detach(socket: SocketLike | null, code: number, reason: string): void {
    if (!socket) return
    socket.onopen = null
    socket.onmessage = null
    socket.onclose = null
    socket.onerror = null
    try {
      socket.close(code, reason)
    } catch {
      /* Closing an already-closing socket is not an error worth surfacing. */
    }
  }

  private notifyState(): void {
    this.snapshot = { connection: this.connection, subscriptions: new Set(this.confirmed) }
    for (const listener of this.stateListeners) listener()
  }

  private setConnection(next: ConnectionState): void {
    if (this.connection === next) return
    this.connection = next
    this.notifyState()
  }

  private open(): void {
    const token = this.options.getToken()
    if (!token) {
      // No session yet; `connect()` is called again after the bootstrap lands.
      this.setConnection('closed')
      return
    }
    this.setConnection(this.backoffIndex === 0 ? 'connecting' : 'reconnecting')

    let socket: SocketLike
    try {
      socket = this.options.createSocket(socketUrl(this.options.path, token))
    } catch {
      this.scheduleReconnect()
      return
    }
    this.socket = socket

    socket.onopen = () => {
      // `hello`, not `open`, is the real handshake success: the API can still
      // reject the token before the upgrade completes.
      this.startPing()
    }
    socket.onmessage = (event) => this.handleRaw(event.data)
    socket.onerror = () => {
      /* `onclose` always follows; nothing to do here. */
    }
    socket.onclose = (event) => this.handleClose(event.code)
  }

  private handleRaw(data: unknown): void {
    if (typeof data !== 'string') return
    let parsed: unknown
    try {
      parsed = JSON.parse(data)
    } catch {
      return
    }
    if (!parsed || typeof parsed !== 'object') return
    const type = (parsed as { type?: unknown }).type
    // Only the envelope is checked, never the payload: the union may grow
    // server-side ahead of the panel (API.md §21.3), and running a full zod
    // parse here would silently blank the live feed on any additive DTO change
    // instead of degrading to "one unknown field the UI ignores".
    if (typeof type !== 'string' || !KNOWN_EVENT_TYPES.has(type)) return
    const message = parsed as WsServerMessage

    switch (message.type) {
      case 'hello':
        this.backoffIndex = 0
        this.setConnection('open')
        this.confirmed = new Set()
        this.flushSubscriptions()
        break
      case 'subscribed':
        this.confirmed = new Set(message.data.serverIds)
        this.notifyState()
        break
      case 'pong':
        this.clearTimer('pong')
        break
      case 'error':
        // A `FORBIDDEN` on subscribe means that id is not ours; drop it so the
        // next flush does not ask again.
        if (message.data.code === 'FORBIDDEN') this.dropForbiddenSubscriptions()
        break
      default:
        break
    }

    for (const handler of this.messageHandlers) handler(message)
  }

  private dropForbiddenSubscriptions(): void {
    // The error message does not name the id in a machine-readable way, so the
    // authoritative set is whatever the next `subscribed` confirms; until then
    // stop re-sending the whole desired set.
    this.desired = new Set(this.confirmed)
  }

  private handleClose(code: number): void {
    this.socket = null
    this.clearTimer('ping')
    this.clearTimer('pong')

    if (this.stopped || code === WS_CLOSE_CODES.NORMAL) {
      this.setConnection('closed')
      return
    }

    if (code === WS_CLOSE_CODES.TOKEN_EXPIRED) {
      // Same single-flight refresh the REST layer uses, so an expiring token
      // never produces two `POST /v1/auth/refresh` calls.
      this.setConnection('reconnecting')
      this.options
        .refresh()
        .then(() => {
          if (!this.stopped) this.open()
        })
        .catch(() => {
          this.stopped = true
          this.setConnection('closed')
          this.options.onSessionLost()
        })
      return
    }

    if (code === WS_CLOSE_CODES.POLICY_VIOLATION) {
      // Flooded or a 6th socket: stop, surface `closed` with the manual
      // reconnect action, and do not retry for 30 s.
      this.setConnection('closed')
      this.reconnectTimer = this.options.setTimer(() => {
        this.reconnectTimer = null
        if (!this.stopped) this.open()
      }, WS_LIMITS.policyViolationBackoffMs)
      return
    }

    this.scheduleReconnect()
  }

  private scheduleReconnect(): void {
    this.setConnection('reconnecting')
    const ladder = WS_LIMITS.reconnectBackoffMs
    const base = ladder[Math.min(this.backoffIndex, ladder.length - 1)] ?? 30_000
    this.backoffIndex += 1
    // ±20 % jitter so a server restart does not bring every panel back at once.
    const jitter = 1 + (this.options.random() * 0.4 - 0.2)
    this.reconnectTimer = this.options.setTimer(() => {
      this.reconnectTimer = null
      if (!this.stopped) this.open()
    }, Math.round(base * jitter))
  }

  private flushSubscriptions(): void {
    if (!this.socket || this.socket.readyState !== SOCKET_OPEN) return
    const added = [...this.desired].filter((id) => !this.confirmed.has(id))
    const removed = [...this.confirmed].filter((id) => !this.desired.has(id))
    if (added.length > 0) this.send({ op: 'subscribe', serverIds: added })
    if (removed.length > 0) this.send({ op: 'unsubscribe', serverIds: removed })
  }

  private send(message: WsClientMessage): void {
    if (!this.socket || this.socket.readyState !== SOCKET_OPEN) return
    this.socket.send(JSON.stringify(message))
  }

  private startPing(): void {
    this.clearTimer('ping')
    const tick = (): void => {
      this.send({ op: 'ping' })
      // Browsers cannot emit raw ping frames, so liveness is the app-level
      // `pong`: none within 10 s means the socket is dead even though the
      // readyState still says OPEN (API.md §21.4).
      this.clearTimer('pong')
      this.pongTimer = this.options.setTimer(() => {
        this.pongTimer = null
        const socket = this.socket
        this.socket = null
        this.detach(socket, WS_CLOSE_CODES.GOING_AWAY, 'pong timeout')
        this.handleClose(WS_CLOSE_CODES.GOING_AWAY)
      }, WS_LIMITS.pongTimeoutMs)
      this.pingTimer = this.options.setTimer(tick, WS_LIMITS.clientPingIntervalMs)
    }
    this.pingTimer = this.options.setTimer(tick, WS_LIMITS.clientPingIntervalMs)
  }

  private clearTimer(which: 'reconnect' | 'ping' | 'pong' | 'subscribe'): void {
    switch (which) {
      case 'reconnect':
        if (this.reconnectTimer !== null) this.options.clearTimer(this.reconnectTimer)
        this.reconnectTimer = null
        return
      case 'ping':
        if (this.pingTimer !== null) this.options.clearTimer(this.pingTimer)
        this.pingTimer = null
        return
      case 'pong':
        if (this.pongTimer !== null) this.options.clearTimer(this.pongTimer)
        this.pongTimer = null
        return
      case 'subscribe':
        if (this.subscribeTimer !== null) this.options.clearTimer(this.subscribeTimer)
        this.subscribeTimer = null
        return
    }
  }

  private clearAllTimers(): void {
    this.clearTimer('reconnect')
    this.clearTimer('ping')
    this.clearTimer('pong')
    this.clearTimer('subscribe')
  }
}

/** One socket per browser tab, a module singleton, never held in React state. */
export const wsClient = new WsClient()
